import React, { createContext, useState, useContext, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { documentService } from '../services/documentService';

// Define permission types
export const PERMISSIONS = {
  VIEW: 'view',
  SIGN: 'sign',
  COMMENT: 'comment',
  EDIT: 'edit',
  SHARE: 'share',
  ADMIN: 'admin',
};

const PermissionsContext = createContext(null);

export const PermissionsProvider = ({ children }) => {
  const { user } = useAuth();
  const [userPermissions, setUserPermissions] = useState({
    documents: {},
    folders: {},
    teams: {},
  });
  const [permissionsLoaded, setPermissionsLoaded] = useState(false);
  
  // Fetch user documents to extract permissions when the user changes
  useEffect(() => {
    const fetchDocumentPermissions = async () => {
      if (!user) {
        setUserPermissions({
          documents: {},
          folders: {},
          teams: {},
        });
        setPermissionsLoaded(true);
        return;
      }
      
      try {
        const documentsResponse = await documentService.getUserDocuments();
        const documents = documentsResponse.documents || documentsResponse || [];
        
        // Extract permissions from document data
        const documentPermissions = {};
        
        documents.forEach(doc => {
          const permissions = [];
          
          // Every document you can access has at least VIEW permission
          permissions.push(PERMISSIONS.VIEW);
          
          // Check ownership using ID comparison (more reliable than email)
          const isOwner = doc.ownerId === user.id || 
                          doc.owner?.id === user.id || 
                          doc.documentOwner?.id === user.id ||
                          doc.isOwner === true;
          
          if (isOwner) {
            // Document owners have all permissions
            permissions.push(PERMISSIONS.SIGN);
            permissions.push(PERMISSIONS.EDIT);
            permissions.push(PERMISSIONS.SHARE);
            permissions.push(PERMISSIONS.ADMIN);
          } else {
            // Add permissions based on document properties for shared documents
            if (doc.permissions?.canSign || doc.permission === 'sign') {
              permissions.push(PERMISSIONS.SIGN);
            }
            
            if (doc.permissions?.canEdit || doc.permission === 'edit') {
              permissions.push(PERMISSIONS.EDIT);
            }
            
            if (doc.permissions?.canShare || doc.permission === 'share') {
              permissions.push(PERMISSIONS.SHARE);
            }
          }
          
          documentPermissions[doc.id] = permissions;
        });
        
        setUserPermissions(prev => ({
          ...prev,
          documents: documentPermissions
        }));
        
        setPermissionsLoaded(true);
      } catch (error) {
        console.error('Error fetching document permissions:', error);
        setPermissionsLoaded(true);
      }
    };
    
    fetchDocumentPermissions();
  }, [user]);
  
  // Check if user has a specific permission for a document
  const hasDocumentPermission = (documentId, permission) => {
    if (!user) return false;
    
    // If document not in cache and permissions are loaded, refresh but don't assume access
    if (!userPermissions.documents[documentId] && permissionsLoaded) {
      // Refresh permissions in background for future calls, but don't grant access
      refreshPermissions();
      return false; // 🔒 Secure: Don't grant access until backend confirms
    }
    
    if (!userPermissions.documents[documentId]) return false;
    
    return userPermissions.documents[documentId].includes(permission);
  };

  // Method to refresh permissions (useful after document upload/sharing)
  const refreshPermissions = async () => {
    if (!user) return;
    
    try {
      const response = await documentService.getUserDocuments();
      
      // Handle response structure - it could be an array or an object with documents array
      const documents = Array.isArray(response) ? response : (response?.documents || []);
      
      // Extract permissions from document data
      const documentPermissions = {};
      
      documents.forEach(doc => {
        const permissions = [];
        
        // Every document you can access has at least VIEW permission
        permissions.push(PERMISSIONS.VIEW);
        
        // Check ownership using ID comparison (more reliable than email)
        const isOwner = doc.ownerId === user.id || 
                        doc.owner?.id === user.id || 
                        doc.documentOwner?.id === user.id ||
                        doc.isOwner === true;
        
        if (isOwner) {
          // Document owners have all permissions
          permissions.push(PERMISSIONS.SIGN);
          permissions.push(PERMISSIONS.EDIT);
          permissions.push(PERMISSIONS.SHARE);
          permissions.push(PERMISSIONS.ADMIN);
        } else {
          // Add permissions based on document properties for shared documents
          if (doc.permissions?.canSign || doc.permission === 'sign') {
            permissions.push(PERMISSIONS.SIGN);
          }
          
          if (doc.permissions?.canEdit || doc.permission === 'edit') {
            permissions.push(PERMISSIONS.EDIT);
          }
          
          if (doc.permissions?.canShare || doc.permission === 'share') {
            permissions.push(PERMISSIONS.SHARE);
          }
        }
        
        documentPermissions[doc.id] = permissions;
      });
      
      setUserPermissions(prev => ({
        ...prev,
        documents: documentPermissions
      }));
    } catch (error) {
      console.error('Error refreshing permissions:', error);
    }
  };
  
  // Get all users with permissions for a document
  const getDocumentUsers = async (documentId) => {
    try {
      const users = await documentService.getDocumentPermissions(documentId);
      return users;
    } catch (error) {
      console.error('Error fetching document users:', error);
      return [];
    }
  };
  
  // Grant document permission to a user
  const grantDocumentPermission = async (documentId, email, permission) => {
    try {
      await documentService.shareDocument(documentId, email, [permission]);
      return true;
    } catch (error) {
      console.error('Error granting permission:', error);
      return false;
    }
  };
  
  // Revoke document permission from a user
  const revokeDocumentPermission = async (documentId, userId, permission) => {
    try {
      await documentService.revokePermission(documentId, userId, permission);
      return true;
    } catch (error) {
      console.error('Error revoking permission:', error);
      return false;
    }
  };
  
  return (
    <PermissionsContext.Provider
      value={{
        userPermissions,
        permissionsLoaded,
        hasDocumentPermission,
        refreshPermissions,
        getDocumentUsers,
        grantDocumentPermission,
        revokeDocumentPermission,
        PERMISSIONS,
      }}
    >
      {children}
    </PermissionsContext.Provider>
  );
};

export const usePermissions = () => useContext(PermissionsContext);